import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import type { ReceiptOrder } from '@/api/warehouse'

const orderStatusText: Record<string, string> = {
  bestellt: 'bestellt',
  in_abwicklung: 'in Abwicklung',
}

interface Props {
  value: string
  orders: ReceiptOrder[]
  open: boolean
  onChange: (value: string) => void
  onOpenChange: (open: boolean) => void
}

export function ReceiptOrderSelect({ value, orders, open, onChange, onOpenChange }: Props) {
  const root = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(-1)
  const query = value.trim().toLocaleLowerCase()
  const matches = orders.filter((order) =>
    [order.orderNumber, order.lieferant].some((text) => text?.toLocaleLowerCase().includes(query)),
  )

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [onOpenChange])

  function choose(order: ReceiptOrder) {
    onChange(order.orderNumber)
    onOpenChange(false)
    setActive(-1)
  }

  return (
    <div className="relative" ref={root}>
      <label className="text-sm" htmlFor="receipt-order-input">
        Bestellnummer
      </label>
      <Input
        id="receipt-order-input"
        value={value}
        placeholder="leer = keine Bestellnummer"
        role="combobox"
        aria-expanded={open}
        aria-controls="receipt-order-options"
        aria-autocomplete="list"
        aria-activedescendant={
          open && active >= 0 && matches[active] ? `receipt-order-${matches[active].id}` : undefined
        }
        onFocus={() => {
          setActive(-1)
          onOpenChange(true)
        }}
        onChange={(event) => {
          onChange(event.target.value)
          setActive(-1)
          onOpenChange(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onOpenChange(false)
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            onOpenChange(true)
            if (matches.length)
              setActive((current) =>
                event.key === 'ArrowDown'
                  ? Math.min(current + 1, matches.length - 1)
                  : current < 0
                    ? matches.length - 1
                    : Math.max(current - 1, 0),
              )
          } else if (event.key === 'Enter' && open && matches[active]) {
            event.preventDefault()
            choose(matches[active])
          }
        }}
      />
      {open && (
        <div
          id="receipt-order-options"
          role="listbox"
          aria-label="Offene Bestellungen"
          className="absolute z-20 mt-1 max-h-64 w-96 max-w-[90vw] overflow-auto rounded-md border border-line bg-panel"
        >
          {matches.map((order, index) => (
            <div
              id={`receipt-order-${order.id}`}
              key={order.id}
              role="option"
              aria-selected={index === active}
              className={`cursor-pointer px-3 py-2 text-sm ${index === active ? 'bg-primary-soft' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(order)}
            >
              <strong>{order.orderNumber}</strong> · {order.lieferant ?? 'Ohne Lieferant'} ·{' '}
              {order.positionen} Pos.{' '}
              <span className="rounded bg-primary-soft px-1.5 py-0.5 text-xs text-primary">
                {orderStatusText[order.status] ?? order.status}
              </span>
            </div>
          ))}
          {!matches.length && (
            <div className="px-3 py-2 text-sm text-muted">Keine passenden Bestellungen</div>
          )}
        </div>
      )}
    </div>
  )
}
